import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, isSessionError } from "@/lib/api";
import { ROLE_CLS, ROLE_LABEL } from "@/lib/staff";
import { FilterSelect, FilterInput, Pager, ExportButton, fmtWIB, cleanParams } from "@/components/admin/LogTools";

const EMPTY = { date_from: "", date_to: "", actor: "", action: "", q: "" };

const ActivityRow = ({ r }) => (
  <tr className="border-t border-white/5 bg-[#0d0d12]" data-testid="activity-row">
    <td className="whitespace-nowrap px-4 py-3 text-zinc-400">{fmtWIB(r.at)}</td>
    <td className="whitespace-nowrap px-4 py-3">
      <span className="font-medium">{r.actor_name}</span>{" "}
      {ROLE_LABEL[r.actor_role] ? <span className={`ml-1 rounded px-1.5 py-0.5 text-[9px] font-bold ${ROLE_CLS[r.actor_role]}`}>{ROLE_LABEL[r.actor_role]}</span> : null}
    </td>
    <td className="whitespace-nowrap px-4 py-3 font-semibold text-[#ffd700]">{r.action}</td>
    <td className="px-4 py-3">{r.target || "—"}</td>
    <td className="px-4 py-3 text-xs text-zinc-400">{r.details || "—"}</td>
  </tr>
);

export const ActivityPanel = () => {
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const params = cleanParams(f);
  const key = JSON.stringify(params);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api.get("/staff/activity", { params: { ...JSON.parse(key), page } }).then((r) => live && setData(r.data))
        .catch((e) => live && !isSessionError(e) && toast.error(errMsg(e)));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [key, page]);

  const set = (k, v) => {
    setF((x) => ({ ...x, [k]: v }));
    setPage(1);
  };

  return (
    <section className="space-y-4" data-testid="activity-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-xs text-zinc-500">Catatan permanen semua tindakan admin (login, perubahan pengaturan, hak akses, export). Waktu dalam WIB.</p>
        <ExportButton url="/staff/activity/export" params={params} testid="activity-export" />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap items-end gap-3" data-testid="activity-filters">
          <FilterInput label="Dari tanggal" type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} testid="activity-date-from" />
          <FilterInput label="Sampai tanggal" type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} testid="activity-date-to" />
          <FilterSelect label="Pelaku" value={f.actor} onChange={(v) => set("actor", v)} options={(data?.actor_options || []).map((a) => [a, a])} testid="activity-actor" />
          <FilterSelect label="Tindakan" value={f.action} onChange={(v) => set("action", v)} options={(data?.action_options || []).map((a) => [a, a])} testid="activity-action" />
          <FilterInput label="Cari" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Target / detail…" testid="activity-search" />
          <button onClick={() => { setF(EMPTY); setPage(1); }} className="rk-btn-ghost inline-flex items-center gap-1 !py-2 text-xs" data-testid="activity-reset"><RotateCcw size={13} /> Reset</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/5">
        <table className="w-full text-sm">
          <thead className="bg-[#13131a] text-[11px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left">Waktu (WIB)</th>
              <th className="px-4 py-3 text-left">Pelaku</th>
              <th className="px-4 py-3 text-left">Tindakan</th>
              <th className="px-4 py-3 text-left">Target</th>
              <th className="px-4 py-3 text-left">Detail</th>
            </tr>
          </thead>
          <tbody>
            {!data ? null : data.items.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Belum ada aktivitas.</td></tr>
            ) : data.items.map((r) => <ActivityRow key={r.id} r={r} />)}
          </tbody>
        </table>
      </div>
      {data ? <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} /> : null}
    </section>
  );
};
